import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-xl font-medium transition-premium hover-lift active-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap leading-tight',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-primary text-text-primary hover:bg-brand-primary-hover focus-visible:ring-brand-primary',
        secondary:
          'bg-brand-secondary text-text-primary hover:bg-brand-secondary-hover focus-visible:ring-brand-secondary',
        success:
          'bg-success-500 text-text-primary hover:bg-success-600 active:bg-success-700 focus-visible:ring-success-500',
        danger:
          'bg-error-500 text-text-primary hover:bg-error-600 active:bg-error-700 focus-visible:ring-error-500',
        warning:
          'bg-warning-500 text-text-primary hover:bg-warning-600 active:bg-warning-700 focus-visible:ring-warning-500',
        ghost:
          'text-text-primary hover:bg-bg-hover active:bg-bg-tertiary',
        link: 'text-brand-primary underline-offset-4 hover:underline hover:text-brand-primary-hover',
        outline:
          'border border-border-primary text-text-primary hover:bg-bg-hover active:bg-bg-tertiary focus-visible:ring-brand-primary',
      },
      size: {
        sm: 'h-11 px-3 text-sm', // 44px all devices (WCAG AAA compliant)
        md: 'h-12 px-4 text-sm', // 48px all devices (WCAG AAA compliant)
        lg: 'h-14 px-6 text-base', // 56px all devices (WCAG AAA compliant)
        icon: 'h-10 w-10', // 40px square for icon-only buttons
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * Button component with multiple variants and sizes
 * @param variant - Button style variant (primary, secondary, success, danger, warning, ghost, link, outline)
 * @param size - Button size (sm, md, lg)
 * @param isLoading - Show loading state
 * @param icon - Icon element to display
 * @param iconPosition - Position of icon relative to text (left, right)
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isLoading = false,
      icon,
      iconPosition = 'left',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        <AnimatePresence mode="wait">
          {isLoading && (
            <motion.svg
              key="spinner"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              className={cn('h-4 w-4 animate-spin', {
                'me-2': children,
              })}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </motion.svg>
          )}
        </AnimatePresence>

        {!isLoading && icon && iconPosition === 'left' && (
          <motion.span
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            className="me-2 flex items-center"
          >
            {icon}
          </motion.span>
        )}

        <span className={cn({ 'opacity-0': isLoading && !children })}>
          {children}
        </span>

        {!isLoading && icon && iconPosition === 'right' && (
          <motion.span
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            className="ms-2 flex items-center"
          >
            {icon}
          </motion.span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };

